import {
  type SpaceApiKey,
  SpaceApiKeyService,
  SpaceApiKeyStatePhase,
  type SpaceApiKeyValid,
} from '@qdrant/qdrant-cloud-public-api/serverless/space/auth/v1/space_api_key';
import {
  listSpaceApiKeys,
  createSpaceApiKey,
  deleteSpaceApiKey,
} from '@qdrant/qdrant-cloud-public-api/serverless/space/auth/v1/space_api_key/connect-query';
import { transport } from '@/api/transport';
import {
  createConnectQueryKey,
  createServiceQueries,
  type DeepOmitProtobufInternals,
  type MutationFactory,
  queryOptions,
  useMutation,
} from '@/connect-query';

// GRPC types
export type SpaceApiKeyDto = DeepOmitProtobufInternals<SpaceApiKeyValid>;

/** Phases the backend is still working through: a key in one of them is about to change. */
const BUSY_PHASES: ReadonlySet<SpaceApiKeyStatePhase> = new Set([
  SpaceApiKeyStatePhase.PROCESSING,
  SpaceApiKeyStatePhase.DELETING,
]);

export const isSpaceApiKeyBusy = ({ state }: SpaceApiKey) =>
  BUSY_PHASES.has(state?.phase ?? SpaceApiKeyStatePhase.UNSPECIFIED);

export const spaceApiKeyService = createServiceQueries(SpaceApiKeyService, {
  listSpaceApiKeysQuery: (params) => queryOptions(listSpaceApiKeys, params, { transport }),
});

export const useCreateSpaceApiKeyMutation: MutationFactory<typeof createSpaceApiKey> = () =>
  useMutation(createSpaceApiKey, {
    onSuccess: (data, _variables, _onMutateResult, { client }) =>
      client.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listSpaceApiKeys,
          input: { accountId: data.spaceApiKey.accountId, spaceId: data.spaceApiKey.spaceId },
        }),
      }),
  });

export const useDeleteSpaceApiKeyMutation: MutationFactory<typeof deleteSpaceApiKey> = () =>
  useMutation(deleteSpaceApiKey, {
    onSuccess: (_data, { accountId, spaceId }, _onMutateResult, { client }) =>
      client.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listSpaceApiKeys,
          input: { accountId, spaceId },
        }),
      }),
  });
